import { zodResolver } from '@hookform/resolvers/zod'
import {
  BuildingsIcon,
  CarSimpleIcon,
  CheckIcon,
  EmptyIcon,
  Icon as PhosphorIcon,
  PlusIcon,
  TrashIcon,
  UserCircleIcon,
} from '@phosphor-icons/react'
import { isAxiosError } from 'axios'
import { TFunction } from 'i18next'
import { useEffect, useMemo, useRef, useState } from 'react'
import { FormProvider, useFieldArray, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { getClientsWithCount } from '@/api/clients/get-clients-with-count'
import {
  getBuildingUnits,
  getResidentVehicles,
  useFetchResidentVehicles,
  useUpdateParkingSpot,
} from '@/api/parking-management'
import { Button } from '@/components/ui/button'
import { ConfirmationPopup } from '@/components/ui/confirmation-popup'
import { Divider } from '@/components/ui/divider'
import { Icon } from '@/components/ui/icon'
import { Modal } from '@/components/ui/modal'
import { NavbarMenu, NavbarMenuItem } from '@/components/ui/navbar-menu'
import { Select, SelectOption, useSelectOptions } from '@/components/ui/select'
import { TextInput } from '@/components/ui/text-input'
import { Typography } from '@/components/ui/typography'
import { popupFactory, toastFactory } from '@/factories'
import { ParkingAssignmentType, ParkingAssignmentTypeEnum, ResidentParkingSpot } from '@/models'
import { formatDateOnly, getApiErrorMessage } from '@/utils'

import { getAssignmentTypeLabels } from '../residents-parking.constants'
import { MAX_VEHICLES } from './parking-modals.constants'
import * as S from './parking-modals.styled'
import {
  makeParkingSpotFormSchema,
  ParkingSpotFormData,
  VehicleFormData,
} from './parking-modals.types'
import {
  buildVehicleDetails,
  getEmptyVehicle,
  getInitialResidentVehicles,
  getInitialUnitVehicles,
  isDuplicatedLicensePlatesError,
  isEmptyVehicle,
  isOccupiedLicensePlateError,
  isVehicleApiError,
  mapApiVehicleToFormData,
  openAssociateVehiclePopup,
  parseCanAssociateError,
  trimEmptyVehicles,
} from './parking-modals.utils'
import { VehiclesForm } from './vehicles-form'

const mapVehicle = (vehicle: VehicleFormData, clientId?: number) => ({
  clientId,
  color: vehicle.color || undefined,
  id: vehicle.id,
  licensePlate: vehicle.licensePlate,
  make: vehicle.make || undefined,
  model: vehicle.model || undefined,
  permitTag: vehicle.permitTag || undefined,
  year: vehicle.year ? Number(vehicle.year) : undefined,
})

const getAssignmentTypeOptions = (
  t: TFunction<['parking-management']>,
): {
  icon: PhosphorIcon
  label: string
  value: ParkingAssignmentType
}[] => {
  const labels = getAssignmentTypeLabels(t)
  return [
    { icon: EmptyIcon, label: labels.Unassigned, value: ParkingAssignmentTypeEnum.enum.Unassigned },
    { icon: BuildingsIcon, label: labels.Unit, value: ParkingAssignmentTypeEnum.enum.Unit },
    {
      icon: UserCircleIcon,
      label: labels.Resident,
      value: ParkingAssignmentTypeEnum.enum.Resident,
    },
  ]
}

type ParkingSpotModalProps = {
  parkingSpot: ResidentParkingSpot
  onClose: () => void
}

const deriveAssignmentType = (parkingSpot: ResidentParkingSpot): ParkingAssignmentType => {
  if (parkingSpot.assignmentType) {
    if (ParkingAssignmentTypeEnum.options.includes(parkingSpot.assignmentType)) {
      return parkingSpot.assignmentType as ParkingAssignmentType
    }
  }
  return parkingSpot.clientId
    ? ParkingAssignmentTypeEnum.enum.Resident
    : ParkingAssignmentTypeEnum.enum.Unassigned
}

export const ParkingSpotModal = ({ parkingSpot, onClose }: ParkingSpotModalProps) => {
  const { t } = useTranslation(['parking-management'])
  const initialAssignmentType = deriveAssignmentType(parkingSpot)

  const [activeVehicleIndex, setActiveVehicleIndex] = useState(0)
  const populatedForUnit = useRef<string | null>(parkingSpot.unit ?? null)
  const initialBackendData = useRef({
    residentId: parkingSpot.clientId ?? null,
    unit: parkingSpot.unit ?? null,
    vehicles:
      initialAssignmentType === ParkingAssignmentTypeEnum.enum.Unit
        ? getInitialUnitVehicles(parkingSpot)
        : initialAssignmentType === ParkingAssignmentTypeEnum.enum.Resident
          ? getInitialResidentVehicles(parkingSpot)
          : [getEmptyVehicle()],
  })
  const [isUnitVehiclesEmpty, setIsUnitVehiclesEmpty] = useState(() => {
    if (initialAssignmentType !== ParkingAssignmentTypeEnum.enum.Unit) return false
    return initialBackendData.current.vehicles.every(isEmptyVehicle)
  })
  const { isPending: isGetResidentVehiclesPending, mutateAsync: fetchResidentVehiclesMutation } =
    useFetchResidentVehicles()
  const { mutateAsync: updateParkingSpot, isPending: isUpdatePending } = useUpdateParkingSpot()

  const schema = useMemo(() => makeParkingSpotFormSchema(t), [t])

  const form = useForm<ParkingSpotFormData>({
    defaultValues: {
      assignmentType: initialAssignmentType,
      ...initialBackendData.current,
    },
    mode: 'onChange',
    resolver: zodResolver(schema),
  })

  const {
    fields: vehicleFields,
    append,
    replace,
  } = useFieldArray({
    control: form.control,
    keyName: 'vehicleId',
    name: 'vehicles',
  })

  const assignmentType = form.watch('assignmentType')
  const residentId = form.watch('residentId')
  const unit = form.watch('unit')
  const { errors, isSubmitted } = form.formState

  const residentOptions = useSelectOptions(
    {
      query: getClientsWithCount,
      requestData: { buildingId: parkingSpot.buildingId },
      transform: (data) => {
        const activeLabel = t('parking-management:parkingSpotModal.residentStatus.active')
        const inactiveLabel = t('parking-management:parkingSpotModal.residentStatus.inactive')
        const clients = data?.clients ?? []
        const options = clients.map((client) => ({
          active: client.active,
          label: `${client.user.firstName} ${client.user.lastName} (#${client.apartment})`,
          moveInDate: client.moveInDate,
          moveOutDate: client.moveOutDate,
          status: client.active ? activeLabel : inactiveLabel,
          statusColor: client.active ? ('green' as const) : ('neutral' as const),
          value: Number(client.id),
        }))

        const isAssignedResidentMissing =
          parkingSpot.clientId && !options.some((option) => option.value === parkingSpot.clientId)

        if (!isAssignedResidentMissing) return options

        const assignedResident =
          parkingSpot.residents?.find((resident) => resident.id === parkingSpot.clientId) ??
          parkingSpot.unitResidents?.find((resident) => resident.id === parkingSpot.clientId)

        return [
          {
            active: assignedResident?.active,
            label: `${parkingSpot.fullName} (#${parkingSpot.apartment})`,
            moveInDate: assignedResident?.moveInDate ?? null,
            moveOutDate: assignedResident?.moveOutDate ?? null,
            status: assignedResident?.active ? activeLabel : inactiveLabel,
            statusColor: assignedResident?.active ? ('green' as const) : ('neutral' as const),
            value: parkingSpot.clientId,
          },
          ...options,
        ]
      },
    },
    {
      enabled:
        !!parkingSpot.buildingId && assignmentType === ParkingAssignmentTypeEnum.enum.Resident,
      queryKey: ['getClientsWithCount', parkingSpot.buildingId],
    },
  )

  const selectedResident = residentOptions.data?.clients?.find(
    (client) => Number(client.id) === residentId,
  )

  const unitOptions = useSelectOptions(
    {
      query: getBuildingUnits,
      requestData: { buildingId: parkingSpot.buildingId },
      transform: (data): SelectOption<string>[] => {
        const options = (data ?? []).map((unit) => ({
          label: unit.unit,
          value: unit.unit,
        }))

        if (!parkingSpot.unit) return options

        const isAssignedUnitMissing = !options.some((option) => option.value === parkingSpot.unit)

        if (!isAssignedUnitMissing) return options

        return [{ label: parkingSpot.unit, value: parkingSpot.unit }, ...options]
      },
    },
    {
      enabled: !!parkingSpot.buildingId && assignmentType === ParkingAssignmentTypeEnum.enum.Unit,
      queryKey: ['getBuildingUnits', parkingSpot.buildingId],
    },
  )

  const resetToEmpty = () => {
    form.setValue('residentId', null)
    form.setValue('unit', null)
    replace([getEmptyVehicle()])
    setActiveVehicleIndex(0)
    populatedForUnit.current = null
    setIsUnitVehiclesEmpty(false)
  }

  const handleAssignmentTypeChange = (type: ParkingAssignmentType) => {
    if (assignmentType === type) return
    form.setValue('assignmentType', type, { shouldDirty: true })
    if (type === initialAssignmentType) {
      const { residentId, unit, vehicles } = initialBackendData.current
      form.setValue('residentId', residentId)
      form.setValue('unit', unit)
      replace(vehicles)
      setActiveVehicleIndex(0)
      populatedForUnit.current = unit
      setIsUnitVehiclesEmpty(
        type === ParkingAssignmentTypeEnum.enum.Unit && vehicles.every(isEmptyVehicle),
      )
    } else {
      resetToEmpty()
    }
  }

  const handleUnassignResident = () => {
    const residentLabel = selectedResident
      ? `${selectedResident.user.firstName} ${selectedResident.user.lastName} (#${selectedResident.apartment})`
      : `${parkingSpot.fullName} (#${parkingSpot.apartment})`

    popupFactory.open(
      ConfirmationPopup,
      {
        cancelText: t('parking-management:parkingSpotModal.unassignPopup.cancel'),
        children: t('parking-management:parkingSpotModal.unassignPopup.residentBody', {
          residentLabel,
        }),
        confirmText: t('parking-management:parkingSpotModal.unassignPopup.confirm'),
        onConfirm: () => {
          form.reset(
            {
              assignmentType: assignmentType,
              residentId: null,
              unit: null,
              vehicles: [getEmptyVehicle()],
            },
            { keepDefaultValues: true },
          )
          replace([getEmptyVehicle()])
          setActiveVehicleIndex(0)
        },
        title: t('parking-management:parkingSpotModal.unassignPopup.title'),
        variant: 'warning',
      },
      { suspendCurrent: true },
    )
  }

  const handleUnassignUnit = () => {
    popupFactory.open(
      ConfirmationPopup,
      {
        cancelText: t('parking-management:parkingSpotModal.unassignPopup.cancel'),
        children: t('parking-management:parkingSpotModal.unassignPopup.unitBody', {
          unit: unit ?? '',
        }),
        confirmText: t('parking-management:parkingSpotModal.unassignPopup.confirm'),
        onConfirm: () => {
          form.reset(
            {
              assignmentType: assignmentType,
              residentId: null,
              unit: null,
              vehicles: [getEmptyVehicle()],
            },
            { keepDefaultValues: true },
          )
          replace([getEmptyVehicle()])
          setActiveVehicleIndex(0)
        },
        title: t('parking-management:parkingSpotModal.unassignPopup.title'),
        variant: 'warning',
      },
      { suspendCurrent: true },
    )
  }

  const handleResidentChange = async (nextResidentId: number) => {
    const residentVehicles = await fetchResidentVehiclesMutation({ residentId: nextResidentId })
    form.setValue('residentId', nextResidentId)
    replace(
      residentVehicles.vehicles.length > 0
        ? residentVehicles.vehicles.map(mapApiVehicleToFormData)
        : [getEmptyVehicle()],
    )
    setActiveVehicleIndex(0)
  }

  const handleUnitChange = (nextUnit: string) => {
    form.setValue('unit', nextUnit, { shouldDirty: true })
    replace([getEmptyVehicle()])
    setActiveVehicleIndex(0)
    populatedForUnit.current = null
  }

  const handleSave = async (formData: ParkingSpotFormData, confirmAssociation?: boolean) => {
    const effectiveAssignmentType =
      (formData.assignmentType === ParkingAssignmentTypeEnum.enum.Unit && !formData.unit) ||
      (formData.assignmentType === ParkingAssignmentTypeEnum.enum.Resident && !formData.residentId)
        ? ParkingAssignmentTypeEnum.enum.Unassigned
        : formData.assignmentType

    if (
      !confirmAssociation &&
      effectiveAssignmentType === ParkingAssignmentTypeEnum.enum.Unit &&
      !noUnitResidents
    ) {
      const missingResidents = formData.vehicles.filter(
        (vehicles) => !vehicles.residentId && !isEmptyVehicle(vehicles),
      )
      if (missingResidents.length > 0) {
        formData.vehicles.forEach((vehicle, index) => {
          if (!vehicle.residentId && !isEmptyVehicle(vehicle)) {
            form.setError(`vehicles.${index}.residentId`, {
              message: t('parking-management:parkingModals.validation.residentRequired'),
              type: 'manual',
            })
          }
        })
        return
      }
    }

    try {
      if (effectiveAssignmentType === ParkingAssignmentTypeEnum.enum.Unassigned) {
        await updateParkingSpot({
          assignmentType: ParkingAssignmentTypeEnum.enum.Unassigned,
          parkingSpotId: parkingSpot.id,
        })
        toastFactory.success(t('parking-management:parkingSpotModal.toastUnassignSuccess'))
        onClose()
        return
      }

      const trimmedVehicles = trimEmptyVehicles(formData.vehicles)

      if (effectiveAssignmentType === ParkingAssignmentTypeEnum.enum.Unit) {
        await updateParkingSpot({
          assignmentType: ParkingAssignmentTypeEnum.enum.Unit,
          clientId: trimmedVehicles[0]?.residentId ?? undefined,
          confirmAssociation,
          parkingSpotId: parkingSpot.id,
          unit: formData.unit!,
          vehicles: trimmedVehicles.map((v) => mapVehicle(v, v.residentId ?? undefined)),
        })
      } else {
        await updateParkingSpot({
          assignmentType: ParkingAssignmentTypeEnum.enum.Resident,
          clientId: formData.residentId!,
          confirmAssociation,
          parkingSpotId: parkingSpot.id,
          vehicles: trimmedVehicles.map((vehicle) => mapVehicle(vehicle, formData.residentId!)),
        })
      }

      if (!confirmAssociation) {
        toastFactory.success(t('parking-management:parkingSpotModal.toastUpdateSuccess'))
      }
      onClose()
    } catch (error) {
      const errorResponse = isAxiosError(error) ? error.response?.data : undefined

      const canAssociateData = parseCanAssociateError(errorResponse)
      if (canAssociateData) {
        const matchingVehicle =
          formData.vehicles.find((vehicle) => !vehicle.id) ?? formData.vehicles[0]
        openAssociateVehiclePopup(buildVehicleDetails(matchingVehicle), t, async () => {
          await handleSave(formData, true)
          toastFactory.success(t('parking-management:parkingSpotModal.toastAssociateSuccess'))
        })
        return
      }

      if (isDuplicatedLicensePlatesError(errorResponse)) {
        const duplicatedPlates = new Set(
          errorResponse.duplicatedLicensePlates.map((p) => p.toLowerCase()),
        )
        formData.vehicles.forEach((vehicle, index) => {
          if (duplicatedPlates.has(vehicle.licensePlate.toLowerCase())) {
            form.setError(`vehicles.${index}.licensePlate`, {
              message: t('parking-management:parkingSpotModal.duplicatedLicensePlateError'),
              type: 'validate',
            })
          }
        })
        return
      }

      if (isOccupiedLicensePlateError(errorResponse)) {
        formData.vehicles.forEach((vehicle, index) => {
          if (!vehicle.id) {
            form.setError(`vehicles.${index}.licensePlate`, {
              message: errorResponse.message,
              type: 'validate',
            })
          }
        })
        return
      }

      if (!isVehicleApiError(errorResponse)) {
        toastFactory.error(getApiErrorMessage(error))
      }
    }
  }

  const handleSubmit = (formData: ParkingSpotFormData) => {
    void handleSave(formData)
  }

  const handleAddVehicle = async () => {
    const isValid = await form.trigger('vehicles')
    if (!isValid) return

    if (assignmentType === ParkingAssignmentTypeEnum.enum.Unit && isUnitVehiclesEmpty) {
      replace([getEmptyVehicle()])
      setIsUnitVehiclesEmpty(false)
      setActiveVehicleIndex(0)
      return
    }
    if (assignmentType === ParkingAssignmentTypeEnum.enum.Unit) {
      append({
        ...getEmptyVehicle(),
        residentId: form.getValues(`vehicles.${activeVehicleIndex}.residentId`),
      })
    } else {
      append(getEmptyVehicle())
    }
    setActiveVehicleIndex(vehicleFields.length)
  }

  const handleRemoveVehicle = () => {
    popupFactory.open(
      ConfirmationPopup,
      {
        children: t('parking-management:parkingSpotModal.removeVehiclePopup.body'),
        confirmText: (
          <>
            <Icon icon={TrashIcon} />{' '}
            {t('parking-management:parkingSpotModal.removeVehiclePopup.confirm')}
          </>
        ),
        onConfirm: async () => {
          if (vehicleFields.length <= 1) {
            replace([getEmptyVehicle()])
            setActiveVehicleIndex(0)
            return
          }

          const nextVehicles = form
            .getValues('vehicles')
            .filter((_, vehicleIndex) => vehicleIndex !== activeVehicleIndex)

          replace(nextVehicles)
          setActiveVehicleIndex((prev) => Math.min(prev, Math.max(0, nextVehicles.length - 1)))

          if (form.formState.errors.vehicles) {
            await form.trigger('vehicles')
          }
        },
        title: t('parking-management:parkingSpotModal.removeVehiclePopup.title'),
        variant: 'danger',
      },
      { suspendCurrent: true },
    )
  }

  const activeVehicle = form.watch(`vehicles.${activeVehicleIndex}`)
  const isActiveVehiclePersisted = Boolean(activeVehicle?.id)

  const isVehicleFormDisabled =
    (assignmentType === ParkingAssignmentTypeEnum.enum.Resident && !residentId) ||
    (assignmentType === ParkingAssignmentTypeEnum.enum.Unit && !unit) ||
    assignmentType === ParkingAssignmentTypeEnum.enum.Unassigned

  const isLoadingVehicles = isGetResidentVehiclesPending
  const isAnyPending = isUpdatePending
  const vehicleErrors = form.formState.errors.vehicles
  const hasLicensePlateError =
    Array.isArray(vehicleErrors) && vehicleErrors.some((vehicles) => vehicles?.licensePlate)
  const showVehiclesForm = assignmentType !== ParkingAssignmentTypeEnum.enum.Unassigned

  const shouldConfirmModalClose = form.formState.isDirty && !isAnyPending

  const handleClose = () => {
    popupFactory.requestCloseWithConfirmation({
      modalInstance: ParkingSpotModal,
      onClose,
      shouldConfirm: shouldConfirmModalClose,
    })
    return false
  }

  const unitResidentsOptions = useSelectOptions(
    {
      query: getClientsWithCount,
      requestData: {
        apartment: unit ?? undefined,
        buildingId: parkingSpot.buildingId,
        filter: 'current',
      },
      transform: (data) =>
        (data?.clients ?? []).map((client) => ({
          active: client.active,
          label: `${client.user.firstName} ${client.user.lastName}`,
          moveInDate: client.moveInDate,
          moveOutDate: client.moveOutDate,
          status: client.active
            ? t('parking-management:parkingSpotModal.residentStatus.active')
            : t('parking-management:parkingSpotModal.residentStatus.inactive'),
          statusColor: client.active ? ('green' as const) : ('neutral' as const),
          value: Number(client.id),
        })),
    },
    {
      enabled:
        !!parkingSpot.buildingId &&
        assignmentType === ParkingAssignmentTypeEnum.enum.Unit &&
        !!unit,
      queryKey: ['getClientsWithCount', parkingSpot.buildingId, unit],
    },
  )
  const noUnitResidents =
    !unitResidentsOptions.selectProps?.options?.length &&
    assignmentType === ParkingAssignmentTypeEnum.enum.Unit
  const canAddVehicle =
    !isAnyPending &&
    !isVehicleFormDisabled &&
    vehicleFields.length < MAX_VEHICLES &&
    !noUnitResidents

  useEffect(() => {
    if (assignmentType !== ParkingAssignmentTypeEnum.enum.Unit || !unit) return
    if (populatedForUnit.current === unit) return
    if (!unitResidentsOptions.data || unitResidentsOptions.isPlaceholderData) return

    populatedForUnit.current = unit

    const residentIds = (unitResidentsOptions.data.clients ?? []).map((c) => Number(c.id))

    if (residentIds.length === 0) {
      replace([getEmptyVehicle()])
      setIsUnitVehiclesEmpty(true)
      setActiveVehicleIndex(0)
      return
    }

    let cancelled = false

    Promise.all(residentIds.map((id) => getResidentVehicles({ residentId: id })))
      .then((results) => {
        if (cancelled) return
        const allVehicles = results.flatMap((result, i) => {
          const residentId = residentIds[i]
          return result.vehicles.map((vehicle) => ({
            ...mapApiVehicleToFormData(vehicle),
            residentId,
          }))
        })
        if (allVehicles.length === 0) {
          replace([getEmptyVehicle()])
          setIsUnitVehiclesEmpty(true)
        } else {
          replace(allVehicles)
          setIsUnitVehiclesEmpty(false)
        }
        setActiveVehicleIndex(0)
      })
      .catch(() => {
        if (cancelled) return
        replace([getEmptyVehicle()])
        setIsUnitVehiclesEmpty(true)
        setActiveVehicleIndex(0)
      })

    return () => {
      cancelled = true
    }
  }, [assignmentType, unit, unitResidentsOptions.data, unitResidentsOptions.isPlaceholderData])

  const hasMoveDates = !!(selectedResident?.moveInDate || selectedResident?.moveOutDate)
  return (
    <Modal
      autoWidth
      size="medium"
      title={t('parking-management:parkingSpotModal.title')}
      closeOnOverlayClick={false}
      closeOnEscapeKey={false}
      headerIcon={CarSimpleIcon}
      footer={
        <>
          {assignmentType !== ParkingAssignmentTypeEnum.enum.Unassigned && (
            <S.AddCarButton
              use="secondary"
              disabled={!canAddVehicle}
              loading={isLoadingVehicles}
              onClick={handleAddVehicle}
            >
              {t('parking-management:parkingSpotModal.addCar')}
              <Icon icon={PlusIcon} />
            </S.AddCarButton>
          )}
          <Button use="secondary" disabled={isAnyPending} onClick={handleClose}>
            {t('parking-management:parkingSpotModal.cancel')}
          </Button>
          <Button
            disabled={!form.formState.isDirty || isAnyPending || hasLicensePlateError}
            loading={isUpdatePending}
            onClick={form.handleSubmit(handleSubmit)}
          >
            {t('parking-management:parkingSpotModal.save')}
            <CheckIcon />
          </Button>
        </>
      }
      onBeforeClose={handleClose}
      onClose={onClose}
    >
      <FormProvider {...form}>
        <S.Content>
          <TextInput
            disabled
            readOnly
            label={t('parking-management:parkingSpotModal.spotLabel')}
            value={parkingSpot.parkingSpot || ''}
          />
          <S.AvailabilityLabelRow>
            <Typography size="text-medium" weight="medium" color="primary800">
              {t('parking-management:parkingSpotModal.availabilityLabel')}
            </Typography>
            <S.AvailabilityTooltip placement="right">
              <div>{t('parking-management:parkingSpotModal.availabilityLegend.unassigned')}</div>
              <div>{t('parking-management:parkingSpotModal.availabilityLegend.unit')}</div>
              <div>{t('parking-management:parkingSpotModal.availabilityLegend.resident')}</div>
            </S.AvailabilityTooltip>
          </S.AvailabilityLabelRow>
          <NavbarMenu mode="tabs">
            {getAssignmentTypeOptions(t).map((option) => (
              <NavbarMenuItem
                key={option.value}
                mode="tabs"
                active={assignmentType === option.value}
                icon={option.icon}
                onClick={() => handleAssignmentTypeChange(option.value)}
              >
                {option.label}
              </NavbarMenuItem>
            ))}
          </NavbarMenu>
          <Divider light />

          <S.DynamicSection>
            {assignmentType === ParkingAssignmentTypeEnum.enum.Resident && (
              <S.ResidentDateSelectContainer $hasDate={!!hasMoveDates}>
                <Select<number>
                  searchable
                  value={residentId}
                  label={t('parking-management:assignmentType.resident')}
                  tooltip={t('parking-management:parkingSpotModal.residentField.tooltip')}
                  placeholder={t('parking-management:parkingSpotModal.residentField.placeholder')}
                  size="medium"
                  name="residentId"
                  {...residentOptions.selectProps}
                  error={isSubmitted ? errors.residentId?.message : undefined}
                  onChange={handleResidentChange}
                />
                <S.UnassignButton
                  use="primary"
                  size="medium"
                  $hasError={isSubmitted && !!errors.residentId}
                  disabled={!residentId || isAnyPending}
                  onClick={handleUnassignResident}
                >
                  {t('parking-management:parkingSpotModal.unassignButton')}
                </S.UnassignButton>
                {hasMoveDates && (
                  <S.MoveInOutDate size="text-small" color="primary500">
                    {t('parking-management:parkingSpotModal.moveInOutDate', {
                      moveIn: selectedResident.moveInDate
                        ? formatDateOnly(selectedResident.moveInDate)
                        : '',
                      moveOut: selectedResident.moveOutDate
                        ? formatDateOnly(selectedResident.moveOutDate)
                        : '',
                    })}
                  </S.MoveInOutDate>
                )}
              </S.ResidentDateSelectContainer>
            )}

            {assignmentType === ParkingAssignmentTypeEnum.enum.Unit && (
              <S.ResidentFieldContainer>
                <Select<string>
                  searchable
                  value={unit}
                  label={t('parking-management:assignmentType.unit')}
                  tooltip={t('parking-management:parkingSpotModal.unitField.tooltip')}
                  size="medium"
                  name="unit"
                  error={isSubmitted ? errors.unit?.message : undefined}
                  {...unitOptions.selectProps}
                  onChange={handleUnitChange}
                />
                <S.UnassignButton
                  use="primary"
                  size="medium"
                  $hasError={isSubmitted && !!errors.unit}
                  disabled={!unit || isAnyPending}
                  onClick={handleUnassignUnit}
                >
                  {t('parking-management:parkingSpotModal.unassignButton')}
                </S.UnassignButton>
              </S.ResidentFieldContainer>
            )}
            <S.VehiclesSectionContainer>
              <S.VehiclesSectionDivider light />
              {showVehiclesForm &&
                (assignmentType === ParkingAssignmentTypeEnum.enum.Unit &&
                !!unit &&
                noUnitResidents ? (
                  <S.NoResidentsPlaceholder>
                    <img
                      src="/images/no-results.svg"
                      alt={t('parking-management:parkingSpotModal.emptyStateImageAlt')}
                    />
                    <Typography size="heading-medium" color="black800">
                      {t('parking-management:parkingSpotModal.noActiveResidents')}
                    </Typography>
                  </S.NoResidentsPlaceholder>
                ) : assignmentType === ParkingAssignmentTypeEnum.enum.Unit &&
                  isUnitVehiclesEmpty ? (
                  <S.NoResidentsPlaceholder>
                    <img
                      src="/images/no-results.svg"
                      alt={t('parking-management:parkingSpotModal.emptyStateImageAlt')}
                    />
                    <Typography size="heading-medium" color="black800">
                      {t('parking-management:parkingSpotModal.noVehiclesLinked')}
                    </Typography>
                  </S.NoResidentsPlaceholder>
                ) : (
                  <S.VehicleFormWrapper>
                    <VehiclesForm
                      activeVehicleIndex={activeVehicleIndex}
                      vehicleFields={vehicleFields}
                      disabled={isVehicleFormDisabled}
                      disabledLicensePlate={isVehicleFormDisabled || isActiveVehiclePersisted}
                      isResidentReadOnly={
                        assignmentType === ParkingAssignmentTypeEnum.enum.Unit &&
                        isActiveVehiclePersisted
                      }
                      residentOptions={
                        assignmentType === ParkingAssignmentTypeEnum.enum.Unit
                          ? unitResidentsOptions.selectProps.options
                          : undefined
                      }
                      onTabChange={setActiveVehicleIndex}
                      onRemoveVehicle={handleRemoveVehicle}
                    />
                  </S.VehicleFormWrapper>
                ))}
            </S.VehiclesSectionContainer>
          </S.DynamicSection>
        </S.Content>
      </FormProvider>
    </Modal>
  )
}
